import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import ModalBase from 'LuxKit/Modal/ModalBase'
import { StyledModalBody } from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Separator from 'LuxKit/Separator'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import AspectRatio from 'components/utils/AspectRatio'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useOfferComparison from 'hooks/Offers/useOfferComparison'
import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import CellContent from './cells/CellContent'
import TextButton from 'LuxKit/Button/TextButton'
import OfferComparisonModalFooter from './OfferComparisonModalFooter'
import ViewOfferButton from './ViewOfferButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import TextInput from 'components/Common/Form/Input/TextInput'
import OfferComparisonModalBody from './OfferComparisonModalBodyYScroller'
import { useElementHeight } from 'hooks/useElementSize'
import { selectOfferComparisonSelections } from 'selectors/offerComparisonSelectors'
import { selectCartOfferComparisonSpoofedSelections, selectCartActiveComparison, selectIsCreatingCartOfferComparison } from 'selectors/cartOfferComparisonSelectors'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { createCartOfferComparison, replaceCartOfferComparisonSelection } from 'actions/CartOfferComparisonActions'
import { removeOfferComparisonSelection } from 'actions/OfferComparisonActions'
import useHeldValue from 'hooks/useHeldValue'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import { LABEL_HEIGHT } from 'LuxKit/Label/constants'
import { forceElementToFirstPosition } from 'lib/array/arrayUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
const imageParams: ImageParams = {
  quality: 'best',
  fit: 'center',
  mobileWidth: '578px',
  tabletWidth: '670px',
  desktopWidth: '410px',
  dpr: 2,
}

// In case we can't get the footer height
const FOOTER_HEIGHT_FALLBACK = 124

const ModalBodyXScroller = styled(StyledModalBody)`
  overflow-y: unset;
  overflow-x: auto;
`

const Row = styled(Group)`
  align-items: stretch;

  & + & {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }

  @supports selector(:has(${CellContent})) {
    &:not(:has(${CellContent}:not(:empty))) {
      display: none;
    }
  }
`

const Cell = styled.div`
  flex: 1;
  min-width: ${rem(240)};
  padding-block: ${rem(12)};

  ${Row}:first-child & {
    padding-top: 0;
  }

  ${Row}:last-child & {
    padding-bottom: 0;
  }
`

const XScrollerContent = styled.div`
  position: relative;
  width: fit-content;
  min-width: 100%;
  height: 100%;
`

const EmptyCellIndicator = styled.div`
  height: 100%;
  display: none;
  align-items: center;
  color: ${props => props.theme.palette.neutral.default.five};


  &:only-child, ${CellContent}:empty + & {
    display: flex;
  }
`

const LabelContainer = styled.div`
  min-height: ${LABEL_HEIGHT};
`

function reorderImagesWithLeadFirst(images: Array<App.Image>, imageId: string): Array<App.Image> {
  const leadImage = images.find(image => image.id === imageId)
  return forceElementToFirstPosition(images, leadImage)
}

interface Props {
  mode?: 'search' | 'user-cart' | 'spoofed-user-cart'
}

function OfferComparisonModal(props: Props) {
  const { mode = 'search' } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const searchSelections = useAppSelector(selectOfferComparisonSelections)
  const offerComparisonItems = useAppSelector(selectCartActiveComparison)
  const spoofedSelections = useAppSelector(selectCartOfferComparisonSpoofedSelections)
  const isCreatingComparison = useAppSelector(selectIsCreatingCartOfferComparison)
  const comparisonHasError = useAppSelector((state: App.State) => state.cartOfferComparison.error)

  const selections = useMemo(() => {
    if (mode === 'user-cart') return offerComparisonItems || []
    if (mode === 'spoofed-user-cart') return spoofedSelections
    return searchSelections
  }, [mode, searchSelections, offerComparisonItems, spoofedSelections])

  const hasSentToCustomer = useMemo(() => {
    return offerComparisonItems &&
      offerComparisonItems.length > 0 &&
      offerComparisonItems.length === spoofedSelections.length &&
      offerComparisonItems.every((item, index) => item.selectionId === spoofedSelections[index].selectionId)
  }, [spoofedSelections, offerComparisonItems])

  const modalElement = useModalElementContext()

  const close = useCallback(() => { modalElement.resolve() }, [modalElement])

  const _comparisonResult = useOfferComparison(selections)

  // If we've gone from having offers selected to not having them, the user has just deselected the last one and the modal will close.
  // In that case, keep the previous result displaying while the modal transitions out.
  const comparisonResult = useHeldValue(
    _comparisonResult,
    (current, held) => (
      held.status === 'success' && held.comparison.options.length > 0 &&
      current.status === 'success' && current.comparison.options.length === 0
    ),
  )

  const hasCustomCSAgentLabel = useMemo(() => {
    return selections.some(selection => selection.label)
  }, [selections])

  const onClickRemove = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const selectionId = e.currentTarget.dataset.selectionid
    if (selectionId) {
      dispatch(removeOfferComparisonSelection(selectionId))

      if (selections.length < 2) {
        close()
      }
    }
  }, [close, dispatch, selections.length])

  const footerRef = useRef<HTMLDivElement>(null)
  const footerHeight = useElementHeight(footerRef) ?? FOOTER_HEIGHT_FALLBACK
  const spacerHeight = comparisonResult.status === 'success' ? footerHeight : 0

  const offerCount = comparisonResult.status === 'success' ? comparisonResult.comparison.options.length : selections.length

  let modalSize: React.ComponentProps<typeof ModalBase>['size'] = 'L'
  if (offerCount === 1) {
    modalSize = 'S'
  } else if (comparisonResult.status === 'success' && offerCount > 2) {
    modalSize = 'XL'
  }

  const updateOfferComparisonSelection = useCallback((selectionId: string | undefined, updates: Partial<App.OfferComparisonSelection>) => {
    const comparisonItem = spoofedSelections.find(s => s.selectionId === selectionId)

    if (!comparisonItem) return

    const newComparisonItem: App.OfferComparisonSelection = { ...comparisonItem, ...updates }
    dispatch(replaceCartOfferComparisonSelection(newComparisonItem))
  }, [spoofedSelections, dispatch])

  const onChangeCustomLabel = useCallback<React.ChangeEventHandler<HTMLInputElement>>((e) => {
    const selectionId = e.currentTarget.dataset.selectionid
    const label = e.currentTarget.value

    return updateOfferComparisonSelection(selectionId, { label })
  }, [updateOfferComparisonSelection])

  const onChangeImage = useCallback((_: number, image: App.Image | undefined, selectionId: string) => {
    if (!image?.id) return

    return updateOfferComparisonSelection(selectionId, { leadOfferImageId: image.id })
  }, [updateOfferComparisonSelection])

  const onCarouselImageChange = useCallback((selectionId: string) =>
    (index: number, image?: App.Image) => {
      onChangeImage(index, image, selectionId)
    }, [onChangeImage])

  const onSendToCustomer = useCallback(() => {
    dispatch(createCartOfferComparison())
  }, [dispatch])

  const failedToSendMessage = intl.formatMessage({
    id: 'UaZixG0XeOFONWjycUSN',
    defaultMessage: 'Failed to send comparison',
    description: 'Error snackbar message when sending offer comparison fails',
  })

  const comparisonTitle = intl.formatMessage({
    id: 'cBwGBZ62ha4bQhZhFAEO',
    defaultMessage: 'Comparison',
    description: 'Modal header title for the offer comparison modal in the offer comparison modal',
  })

  const labelInputLabel = intl.formatMessage({
    id: 'SB4FIV55z6zrA1I5j45U',
    defaultMessage: 'Label',
    description: 'Text input label for custom offer label in agent offer comparison',
  })

  const labelInputPlaceholder = intl.formatMessage({
    id: 'HnngxrirZxeI0k49QMrT',
    defaultMessage: 'e.g. Best value',
    description: 'Text input placeholder for custom offer label in agent offer comparison',
  })

  const leadPhotoLabel = intl.formatMessage({
    id: 'Dqu4B7jo4MZulxQ3lV-i',
    defaultMessage: 'Lead photo',
    description: 'Status label text when lead photo has been sent to customer in offer comparison',
  })

  const leadWithPhotoLabel = intl.formatMessage({
    id: 'UOLTI6p2i5gdPV5PBNvH',
    defaultMessage: 'Lead with this photo',
    description: 'Status label text prompting agent to select lead photo in offer comparison',
  })

  const removeLabel = intl.formatMessage({
    id: 'umrTUHr8ZZkmb40hMUb_',
    defaultMessage: 'Remove',
    description: 'Button text to remove an offer from the comparison modal',
  })

  const sentLabel = intl.formatMessage({
    id: 'kPDAvCcoiCGoIuQPODKJ',
    defaultMessage: 'Sent',
    description: 'Button text after comparison has been sent to customer',
  })

  const sendToCustomerLabel = intl.formatMessage({
    id: 'PWKTGWl9EWYKhT7i0Jul',
    defaultMessage: 'Send to customer',
    description: 'Action button label to send offer comparison to customer',
  })

  useEffect(() => {
    if (comparisonHasError) {
      showSnackbar(failedToSendMessage, 'critical')
    }
  }, [comparisonHasError, failedToSendMessage])

  return (
    <ModalBase size={modalSize}>
      <ModalHeader title={comparisonTitle} onCloseButtonClick={close} />
      <ModalBodyXScroller>
        <XScrollerContent>
          <OfferComparisonModalBody>
            <ModalContent>
              {comparisonResult.status === 'loading' && <LoadingIndicator inline />}

              {comparisonResult.status === 'error' && <MessageBanner kind="critical">{comparisonResult.message}</MessageBanner>}

              {comparisonResult.status === 'success' && <>
                <Row direction="horizontal" gap={16}>
                  {comparisonResult.comparison.options.map(option => (
                    <Cell key={option.id}>
                      <CellContent>
                        <VerticalSpacer gap={12}>
                          {mode === 'user-cart' && hasCustomCSAgentLabel && (
                            <LabelContainer>
                              {option.selection.label && (
                                <PromotionLabel variant="default">{option.selection.label}</PromotionLabel>
                              )}
                            </LabelContainer>
                          )}
                          {mode === 'spoofed-user-cart' && (
                            <TextInput
                              label={labelInputLabel}
                              name="label"
                              maxLength={25}
                              placeholder={labelInputPlaceholder}
                              data-selectionid={option.selection.selectionId}
                              onChange={onChangeCustomLabel}
                              disabled={hasSentToCustomer || isCreatingComparison}
                            />
                          )}
                          <AspectRatio ratio="3:2">
                            <ImageCarousel
                              images={mode === 'user-cart' && option.selection.leadOfferImageId ? reorderImagesWithLeadFirst(option.images, option.selection.leadOfferImageId) : option.images}
                              arrowBehaviour="interaction-visible"
                              imageParams={imageParams}
                              topLeft={<Group direction="vertical" gap={4}>
                                {mode === 'spoofed-user-cart' && <StatusLabel variant="added">{hasSentToCustomer ? leadPhotoLabel : leadWithPhotoLabel}</StatusLabel>}
                                {mode !== 'search' && <ProductTypeLabel productType={option.offer.productType} />}
                              </Group>}
                              topRight={<TripGuard>
                                <BookmarkButton offer={option.offer} withLabel="never" />
                              </TripGuard>}
                              onImageChange={!hasSentToCustomer && mode === 'spoofed-user-cart' ? onCarouselImageChange(option.selection.selectionId) : undefined}
                            />
                          </AspectRatio>
                          <VerticalSpacer gap={4}>
                            <Caption variant="medium">{option.location}</Caption>
                            <BodyText variant="medium" weight="bold">{option.name}</BodyText>
                            {option.packageName && <BodyText variant="medium">{option.packageName}</BodyText>}
                          </VerticalSpacer>
                        </VerticalSpacer>
                      </CellContent>
                    </Cell>
                  ))}
                </Row>

                {comparisonResult.comparison.rows.map(row => (
                  <Row key={row} direction="horizontal" gap={16}>
                    {comparisonResult.comparison.options.map(offer => (
                      <Cell key={offer.id}>
                        {offer.rows[row]?.element}
                        <EmptyCellIndicator aria-hidden><Separator variant="dash" /></EmptyCellIndicator>
                      </Cell>
                    ))}
                  </Row>
                ))}
              </>}
            </ModalContent>
            <div style={{ height: spacerHeight }}></div>
          </OfferComparisonModalBody>

          {mode !== 'spoofed-user-cart' && (
            <OfferComparisonModalFooter ref={footerRef}>
              {comparisonResult.status === 'success' && <Row direction="horizontal" gap={16}>
                {comparisonResult.comparison.options.map(option => (
                  <Cell key={option.id}>
                    <CellContent>
                      <VerticalSpacer gap={4}>
                        <ViewOfferButton option={option} />
                        {mode === 'search' && <TextButton fit="full-width" kind="tertiary" onClick={onClickRemove} data-selectionid={option.selection.selectionId}>{removeLabel}</TextButton>}
                      </VerticalSpacer>
                    </CellContent>
                  </Cell>
                ))}
              </Row>}
            </OfferComparisonModalFooter>
          )}
        </XScrollerContent>
      </ModalBodyXScroller>
      {mode === 'spoofed-user-cart' && <ModalFooter
        primaryActionProps={{
          children: hasSentToCustomer ? sentLabel : sendToCustomerLabel,
          onClick: onSendToCustomer,
          disabled: hasSentToCustomer || isCreatingComparison,
        }}
      />}
    </ModalBase>
  )
}

export default OfferComparisonModal
